import React from "react";
import { HiChevronDown } from "react-icons/hi";
import { OnboardingAnimatedCheck } from "../../onboarding/OnboardingRows";
import Slider, {
  NIGGLE_IMPACT_RESPONSES,
  NIGGLE_PAIN_RESPONSES,
  getNiggleColor,
} from "./Slider";

/** Card row: label left, onboarding-style animated check right. */
export const FormCheckRow: React.FC<{
  title: string;
  subtitle?: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ title, subtitle, checked, onToggle }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onToggle}
    className="flex w-full min-h-[52px] items-center gap-3 py-3.5 text-left"
  >
    <span className="flex-1 min-w-0">
      <span className="block font-funnel text-[15px] font-medium text-base-content">
        {title}
      </span>
      {subtitle && (
        <span className="block text-xs text-base-content/45 font-grotesk mt-0.5">
          {subtitle}
        </span>
      )}
    </span>
    <OnboardingAnimatedCheck checked={checked} />
  </button>
);

/** Muted "…?" link with a chevron that opens a collapsed card section. */
export const FormDisclosureLink: React.FC<{
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ title, open, onOpenChange }) => (
  <button
    type="button"
    onClick={() => onOpenChange(!open)}
    aria-expanded={open}
    className="inline-flex items-center gap-1 pl-2 pt-3 pb-1 text-xs font-grotesk text-base-content/45 hover:text-base-content/70 transition-colors"
  >
    {title}
    <HiChevronDown
      className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
    />
  </button>
);

interface PainInjurySectionProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pain: boolean;
  onPainChange: (pain: boolean) => void;
  injured: boolean;
  onInjuredChange: (injured: boolean) => void;
  /** False hides the "Add a niggle" row (e.g. edit mode). */
  allowNiggle: boolean;
  addNiggle: boolean;
  onAddNiggleChange: (add: boolean) => void;
  nigglePain: number;
  onNigglePainChange: (value: number) => void;
  niggleImpact: number;
  onNiggleImpactChange: (value: number) => void;
  niggleNotes: string;
  onNiggleNotesChange: (value: string) => void;
  niggleSubtitle?: string;
  className?: string;
}

/**
 * Pain / injury / niggle for the log forms, collapsed behind a muted
 * "Injured or felt a niggle?" link. Expanded, it is one card: the pain and
 * injured rows, then (with pain) "Add a niggle" and its inputs.
 */
const PainInjurySection: React.FC<PainInjurySectionProps> = ({
  open,
  onOpenChange,
  pain,
  onPainChange,
  injured,
  onInjuredChange,
  allowNiggle,
  addNiggle,
  onAddNiggleChange,
  nigglePain,
  onNigglePainChange,
  niggleImpact,
  onNiggleImpactChange,
  niggleNotes,
  onNiggleNotesChange,
  niggleSubtitle = "Logged as a Niggle linked to this log",
  className = "",
}) => (
  <div className={`space-y-3 ${className}`}>
    <FormDisclosureLink
      title="Injured or felt a niggle?"
      open={open}
      onOpenChange={onOpenChange}
    />
    {open && (
      <div className="rounded-[10px] border border-base-300 bg-base-100 px-4">
        <FormCheckRow
          title="Did you feel pain?"
          checked={pain}
          onToggle={() => onPainChange(!pain)}
        />
        <div className="h-px bg-base-300" />
        <FormCheckRow
          title="Were you injured?"
          checked={injured}
          onToggle={() => onInjuredChange(!injured)}
        />
        {pain && allowNiggle && (
          <>
            <div className="h-px bg-base-300" />
            <FormCheckRow
              title="Add a niggle"
              subtitle={niggleSubtitle}
              checked={addNiggle}
              onToggle={() => onAddNiggleChange(!addNiggle)}
            />
            {addNiggle && (
              <div className="space-y-4 pt-1 pb-4">
                <Slider
                  question="How much pain are you experiencing?"
                  label="Pain level"
                  value={nigglePain}
                  onChange={onNigglePainChange}
                  description={
                    NIGGLE_PAIN_RESPONSES[nigglePain - 1] ||
                    "Select a response..."
                  }
                  min={1}
                  max={10}
                  fillColor={getNiggleColor(nigglePain)}
                />
                <Slider
                  label="Impact on performance"
                  question="How much did this impact performance?"
                  value={niggleImpact}
                  onChange={onNiggleImpactChange}
                  description={
                    NIGGLE_IMPACT_RESPONSES[niggleImpact - 1] ||
                    "Select a response..."
                  }
                  min={1}
                  max={10}
                  fillColor={getNiggleColor(niggleImpact)}
                />
                <div className="relative">
                  <label className="absolute left-4 top-2 text-[11px] font-medium uppercase tracking-wider text-base-content/50 pointer-events-none z-10">
                    Notes
                  </label>
                  <textarea
                    className="px-4 pt-7 pb-2.5 rounded-md bg-base-200 border border-transparent focus:border-base-300 h-24 resize-none focus:outline-none focus:ring-0 text-[16px] w-full placeholder:text-base-content/40"
                    placeholder="Describe the location, when it started, what makes it better/worse..."
                    value={niggleNotes}
                    onChange={(e) => onNiggleNotesChange(e.target.value)}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    )}
  </div>
);

export default PainInjurySection;
